import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Grid,
  Typography,
  Box,
  CircularProgress,
  Pagination,
  Tab,
} from '@mui/material';
import {
  Bookmark as BookmarkIcon,
  School as SchoolIcon,
  MenuBook as MenuBookIcon,
} from '@mui/icons-material';
import { coursesAPI, lessonsAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';
import { useTranslation } from 'react-i18next';
import Button from '../components/UIKit/Button';
import Card from '../components/UIKit/Card';
import Tabs from '../components/UIKit/Tabs';
import { toast } from 'react-toastify';

const Bookmarks = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const [bookmarks, setBookmarks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    perPage: 6,
  });
  const [tab, setTab] = useState('lesson');
  const { t } = useTranslation(['common', 'lessons']);

  const fetchBookmarks = useCallback(async (page = 1, type = tab) => {
    try {
      setLoading(true);
      const response = await coursesAPI.getBookmarks({
        page,
        limit: pagination.perPage,
        type,
      });
      setBookmarks(response.data?.bookmarks || []);
      const pag = response.data?.pagination || {};
      setPagination({
        currentPage: pag.currentPage || 1,
        totalPages: pag.totalPages || 1,
        totalItems: pag.totalItems || 0,
        perPage: pag.perPage || pagination.perPage,
      });
    } catch (error) {
      toast.error(t('common:errors.loadError'));
    } finally {
      setLoading(false);
    }
  }, [pagination.perPage, setBookmarks, setPagination, setLoading, t, tab]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login');
      return;
    }
    fetchBookmarks(pagination.currentPage, tab);
  }, [fetchBookmarks, isAuthenticated, navigate, pagination.currentPage, tab]);

  const handleNavigateToLesson = (lesson) => {
    const lessonSlug = lesson.slug || lesson._id;
    navigate(`/lessons/${lessonSlug}`);
  };
  const handleNavigateToCourse = (course) => {
    navigate(`/courses/${course.slug || course._id}`);
  };

  const removeBookmark = async (id) => {
    try {
      if (tab === 'lesson') {
        await lessonsAPI.toggleBookmark(id);
        toast.success(t('lessons:lessonDetail.success.bookmarkRemoved'));
      } else {
        await coursesAPI.toggleBookmark(id);
        toast.success(t('courses:courseDetail.toasts.bookmarkRemoved'));
      }
      fetchBookmarks(pagination.currentPage, tab);
    } catch (error) {
      toast.error(t('lessons:lessonDetail.errors.updateBookmark'));
    }
  };

  const handlePageChange = (event, page) => {
    setPagination((prev) => ({ ...prev, currentPage: page }));
  };

  if (loading) {
    return (
      <Container sx={{ mt: 4, mb: 4 }}>
        <Box display="flex" justifyContent="center" my={4}>
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  return (
    <Container>
      <Box sx={{ my: 4 }}>
        <Typography component="h1" variant="h3" gutterBottom>
          <BookmarkIcon sx={{ mr: 1, verticalAlign: 'middle', fontSize: 32 }} />
          {t('common:bookmarks.title')}
        </Typography>
        <Tabs
          value={tab}
          onChange={(_, v) => {
            setTab(v);
            setPagination((prev) => ({ ...prev, currentPage: 1 }));
          }}
          sx={{ mb: 3 }}
        >
          <Tab value="lesson" label={t('common:bookmarks.tabs.lessons')} />
          <Tab value="course" label={t('common:bookmarks.tabs.courses')} />
        </Tabs>
        {bookmarks.length === 0 ? (
          <Box textAlign="center" py={6}>
            <Typography variant="h6" color="textSecondary" gutterBottom>
              {tab === 'lesson' ? t('common:bookmarks.empty.lessons') : t('common:bookmarks.empty.courses')}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              startIcon={<SchoolIcon />}
              onClick={() => navigate(tab === 'lesson' ? '/lessons' : '/courses')}
              sx={{ mt: 2 }}
            >
              {tab === 'lesson' ? t('common:bookmarks.goToLessons') : t('common:bookmarks.goToCourses')}
            </Button>
          </Box>
        ) : (
          <>
            <Grid container spacing={3}>
              {bookmarks.map((bookmark) => (
                <Grid item xs={12} xl={6} key={bookmark._id}>
                  <Card>
                    <Grid container spacing={2}>
                      <Grid item xs={12}>
                        <Box display="flex" alignItems="flex-start" mb={1}>
                          {tab === 'lesson' ? (
                            <MenuBookIcon sx={{ mr: 1, color: 'secondary.main' }} />
                          ) : (
                            <SchoolIcon sx={{ mr: 1, color: 'primary.main' }} />
                          )}
                          <Typography variant="subtitle1" fontWeight="bold">
                            {tab === 'lesson'
                              ? bookmark.lesson?.title || t('lessons:lessonDetailPage.errors.lessonNotFound')
                              : bookmark.course?.title || t('courses:courseCard.errors.courseNotFound')}
                          </Typography>
                        </Box>
                        {tab === 'lesson' && bookmark.lesson?.duration && (
                          <Typography variant="body2" color="textSecondary">
                            {t('lessons:duration')}: {bookmark.lesson.duration} {t('common:time.minutes')}
                          </Typography>
                        )}
                        <Typography
                          variant="caption"
                          color="textSecondary"
                          display="block"
                          sx={{ mt: 1 }}
                        >
                          {t('lessons:card.created')}:{' '}
                          {new Date(bookmark.createdAt).toLocaleDateString()}
                        </Typography>
                      </Grid>
                      <Grid item xs={12}>
                        <Box
                          display="flex"
                          justifyContent="flex-end"
                          height="100%"
                          alignItems="flex-start"
                        >
                          <Button
                            variant="contained"
                            color="primary"
                            sx={{ mr: 1 }}
                            onClick={() =>
                              tab === 'lesson'
                                ? handleNavigateToLesson(bookmark.lesson)
                                : handleNavigateToCourse(bookmark.course)
                            }
                          >
                            {tab === 'lesson' ? t('common:actions.view') : t('common:actions.view')}
                          </Button>
                          <Button
                            variant="outlined"
                            color="error"
                            onClick={() =>
                              tab === 'lesson'
                                ? removeBookmark(bookmark.lesson?._id)
                                : removeBookmark(bookmark.course?._id)
                            }
                          >
                            {t('common:actions.delete')}
                          </Button>
                        </Box>
                      </Grid>
                    </Grid>
                  </Card>
                </Grid>
              ))}
            </Grid>
            {pagination.totalPages > 1 && (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                mt={4}
              >
                <Pagination
                  count={pagination.totalPages}
                  page={pagination.currentPage}
                  onChange={handlePageChange}
                  color="primary"
                  showFirstButton
                  showLastButton
                />
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mt: 2 }}
                >
                  {t('common:pagination.showing_results_simple')
                    .replace(
                      '$1',
                      (pagination.currentPage - 1) * pagination.perPage + 1
                    )
                    .replace(
                      '$2',
                      Math.min(
                        pagination.currentPage * pagination.perPage,
                        pagination.totalItems
                      )
                    )
                    .replace('$3', pagination.totalItems)}
                </Typography>
              </Box>
            )}
            {pagination.totalPages === 1 && pagination.totalItems > 0 && (
              <Box display="flex" justifyContent="flex-end" mt={2}>
                <Typography variant="body2" color="text.secondary">
                  {t('common:pagination.showing_results_simple')
                    .replace('$1', 1)
                    .replace('$2', pagination.totalItems)
                    .replace('$3', pagination.totalItems)}
                </Typography>
              </Box>
            )}
          </>
        )}
      </Box>
    </Container>
  );
};

export default Bookmarks;
